/**
 * PublisherHub — one destination page for place / topic / author on
 * blog.socialpulse.com. Routes:
 *   /publisher/place/{slug}
 *   /publisher/topic/{slug}       (may contain `/` for subcategory paths)
 *   /publisher/@{authorId}
 *
 * Each hub renders:
 *   • Hub header (name + subscriber count + Follow button)
 *   • Article list (list variant — long-form-friendly)
 *   • Empty state that never breaks the page
 */
import { useEffect, useMemo, useState } from "react";
import { useLocation, useParams, Link } from "react-router-dom";
import { MapPin, Tag, User, Loader2, Users } from "lucide-react";
import SubscribeButton from "@/components/publisher/SubscribeButton";
import "@/components/publisher/publisher.css";

const BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api/publisher/v1`;

async function fetchHub(kind, slug) {
  const path = kind === "author" ? `hub/author/${slug}` : `hub/${kind}/${slug}`;
  const r = await fetch(`${BASE}/${path}`);
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json();
}

const KIND_META = {
  place: { icon: MapPin, label: "PLACE",  targetType: "place",  followLabel: "Follow this place" },
  topic: { icon: Tag,    label: "TOPIC",  targetType: "topic",  followLabel: "Follow this topic" },
  author:{ icon: User,   label: "AUTHOR", targetType: "author", followLabel: "Follow" },
};

export default function PublisherHub({ kind }) {
  const params = useParams();
  const location = useLocation();
  // For topic paths ("agriculture/crops/tomato") we need the whole rest of the URL.
  const rawSlug = useMemo(() => {
    if (kind === "topic") {
      const m = location.pathname.match(/\/publisher\/topic\/(.+)$/);
      return m ? decodeURIComponent(m[1]) : params.slug;
    }
    if (kind === "author") {
      // Strip leading @ if present in URL param
      const raw = params.authorId || params.slug || "";
      return raw.startsWith("@") ? raw.slice(1) : raw;
    }
    return params.slug;
  }, [kind, params, location.pathname]);

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setData(null);
    setError(null);
    fetchHub(kind, rawSlug)
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setError(e.message || "Failed to load"); });
    return () => { alive = false; };
  }, [kind, rawSlug]);

  const meta = KIND_META[kind];
  const Icon = meta?.icon || Tag;

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <div className="text-slate-500">Couldn't load this page: {error}</div>
        <Link to="/" className="text-blue-700 underline text-sm mt-3 inline-block">Back to home</Link>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center flex items-center justify-center gap-2 text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading…
      </div>
    );
  }

  const hub = data.hub || {};
  const articles = data.articles || [];

  return (
    <div className="max-w-[1024px] mx-auto px-4 lg:px-6 py-8" data-testid={`publisher-hub-${kind}`}>
      {/* Hub header */}
      <header className="pb-6 border-b border-slate-200 mb-6">
        <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold flex items-center gap-1.5">
          <Icon className="w-3.5 h-3.5" />
          {meta.label}
        </div>
        <h1
          className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mt-1"
          data-testid="publisher-hub-name"
        >
          {kind === "author" ? hub.name : hub.name}
        </h1>
        {kind === "place" && (hub.admin1 || hub.country_name || hub.country) && (
          <div className="text-sm text-slate-600 mt-1">
            {[hub.admin1, hub.country_name || hub.country].filter(Boolean).join(", ")}
          </div>
        )}
        <div className="flex items-center gap-3 mt-4 flex-wrap">
          <SubscribeButton
            targetType={meta.targetType}
            targetId={hub.slug || rawSlug}
            label={meta.followLabel}
            testId={`publisher-hub-follow-${kind}`}
          />
          <span
            className="inline-flex items-center gap-1 text-xs text-slate-500"
            data-testid="publisher-hub-subscribers"
          >
            <Users className="w-3 h-3" />
            {data.subscriber_count || 0} {data.subscriber_count === 1 ? "follower" : "followers"}
          </span>
          <span className="text-xs text-slate-400" data-testid="publisher-hub-count">
            {articles.length} {articles.length === 1 ? "article" : "articles"}
          </span>
        </div>
      </header>

      {/* Article list */}
      {articles.length === 0 ? (
        <div
          className="text-center py-16 text-slate-500 italic"
          data-testid="publisher-hub-empty"
        >
          No articles here yet — check back soon.
        </div>
      ) : (
        <ul className="divide-y divide-slate-200" data-testid="publisher-hub-list">
          {articles.map((a) => (
            <li key={a.article_id} className="py-5" data-testid={`publisher-hub-article-${a.article_id}`}>
              <a
                href={a.canonical_url}
                target="_blank"
                rel="noopener"
                className="group flex gap-4 items-start"
              >
                {a.cover_image_url && (
                  <img
                    src={a.cover_image_url}
                    alt=""
                    loading="lazy"
                    className="w-32 h-24 sm:w-40 sm:h-28 object-cover rounded-lg shrink-0 bg-slate-100"
                  />
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold flex items-center gap-1.5">
                    <span className="text-blue-700">{a.primary_category}</span>
                    <span>·</span>
                    <span>{a.editorial_label}</span>
                    {a.published_at && (
                      <>
                        <span>·</span>
                        <time dateTime={a.published_at}>
                          {new Date(a.published_at).toLocaleDateString(undefined, {
                            month: "short", day: "numeric", year: "numeric",
                          })}
                        </time>
                      </>
                    )}
                  </div>
                  <h2 className="text-lg sm:text-xl font-bold text-slate-900 group-hover:text-blue-700 leading-tight mt-1">
                    {a.title}
                  </h2>
                  {a.content_format === "note" && a.body_html ? (
                    <div
                      className="text-sm text-slate-700 mt-2 line-clamp-4 [&_p]:my-1 [&_a]:text-blue-700 [&_a]:underline"
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{ __html: a.body_html }}
                    />
                  ) : a.summary ? (
                    <p className="text-sm text-slate-600 line-clamp-2 mt-1.5">{a.summary}</p>
                  ) : null}
                  {kind !== "author" && a.author_id && (
                    <div className="text-xs text-slate-500 mt-2">
                      by{" "}
                      <Link
                        to={`/publisher/author/${a.author_id}`}
                        className="text-slate-700 hover:text-blue-700 hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        @{a.author_id}
                      </Link>
                    </div>
                  )}
                </div>
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
